import React, { useContext, createContext, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getPath, ROOT_DASHBOARD } from "../utils/constants";
import {
  setItemToStorage,
  getItemStorage,
  clearItemStorage,
  decodeJwtToken,
} from "../utils/utils";
import LoadingSection from "../jsx/components/LoadingSection";
import { getAccountContent } from "../services/AccountService";
import { useQuery } from "@tanstack/react-query";
import { homePath } from "../utils/constants";
import { supabase } from "../utils/supabaseClient";

export const AuthContext = createContext({
  userDetail: null,
  role: null,
  isAuthenticated: false,
  isLoadingRefresh: false,
  logout: () => {},
  login: () => {},
  refetchAuth: () => {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const history = useHistory();
  const path = getPath();
  const location = useLocation();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!getItemStorage("userDetails");
  });

  const {
    data: dataAccount,
    isFetching: isLoadingAccount,
    refetch,
  } = useQuery(["getAccountContent"], getAccountContent, {
    enabled: !!isAuthenticated,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const userDetail = getItemStorage("userDetails");
    const currentPath = location.pathname.split("/")[1];

    if (userDetail) {
      setIsAuthenticated(true);
    } else if (!userDetail && !homePath.includes(currentPath || path)) {
      history.push("/login");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      dataAccount?.data.onboardingCompleted === false &&
      path !== "onboarding"
    ) {
      history.push("/onboarding");
    }

    //DY: LATER CHANGE TO DASHBOARD
    // if (path === "") {
    //   window.location.href = "/active-visit";
    // }
    // eslint-disable-next-line
  }, [dataAccount]);

  useEffect(() => {
    if (
      dataAccount?.data.onboardingCompleted === true &&
      path === "onboarding"
    ) {
      history.push(ROOT_DASHBOARD);
    }

    // eslint-disable-next-line
  }, [dataAccount]);

  const handleLogout = async () => {
    setIsLoggingOut(true);

    // Bounded global signOut so Gotrue sends the revocation request before
    // document unload cancels in-flight fetch requests. Capped at 800ms so
    // network degradation never traps the user.
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: "global" }),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
    } catch (e) {
      console.error("[AUTH] supabase signOut failed:", e);
    }

    clearItemStorage();
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (host.includes("preprod.legacy.")) {
      window.location.replace("https://preprod.sistemklinik.com/auth/signout");
    } else if (host.includes("legacy.")) {
      window.location.replace("https://sistemklinik.com/auth/signout");
    } else {
      setIsAuthenticated(false);
      setIsLoggingOut(false);
      history.replace("/login");
    }
  };

  const handleLogin = (tokenDetails) => {
    // Ensure expireDate is set so checkAutoLogin / interceptor work correctly
    const expiresIn = parseInt(tokenDetails.expiresIn, 10) || 3600;
    const enriched = {
      ...tokenDetails,
      expiresIn,
      expireDate: new Date(Date.now() + expiresIn * 1000).toISOString(),
    };
    setItemToStorage("userDetails", enriched);

    // Mirror tokens into supabase-js SDK storage (single source of truth).
    // Fire-and-forget — supabase.auth.setSession writes localStorage synchronously
    // for the default localStorage adapter, so navigation below is safe.
    if (tokenDetails.id_token && tokenDetails.refresh_token) {
      supabase.auth
        .setSession({
          access_token: tokenDetails.id_token,
          refresh_token: tokenDetails.refresh_token,
        })
        .catch((e) => console.error("[AUTH] supabase setSession failed:", e));
    }

    setIsAuthenticated(true);

    if (tokenDetails.onboardingCompleted === false) {
      history.push("/onboarding");
      return;
    }

    //DY: LATER CHANGE TO DASHBOARD
    history.push(ROOT_DASHBOARD);
  };
  const userDetail = getItemStorage("userDetails");
  const role = userDetail
    ? decodeJwtToken(userDetail?.id_token).payload?.auth
    : null;

  const auth = {
    userDetail: dataAccount?.data,
    role,
    isAuthenticated,
    logout: handleLogout,
    login: handleLogin,
    refetchAuth: refetch,
  };

  if (isLoggingOut || isLoadingAccount) {
    return <LoadingSection isPage />;
  }

  return (
    <AuthContext.Provider value={{ ...auth }}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
